(() => {
  'use strict';
  const normalize = value => String(value || '').normalize('NFKD').replace(/\p{Diacritic}/gu, '').toLocaleLowerCase();
  const finishKey = value => {
    const name = normalize(value);
    const paint = name.match(/\bsw\s*(\d{4})\b/);
    return paint ? 'sw:' + paint[1] : name;
  };
  const initGallery = root => {
    if (root.dataset.cwReady) return;
    const form = root.querySelector('[data-cw-gallery-filters]');
    const explorer = root.querySelector('[data-slab-explorer]');
    if (!form || !explorer) return;
    const fields = Object.fromEntries(['door','finish','construction','group','q'].map(name => [name, form.elements.namedItem(name)]));
    if (Object.values(fields).some(field => !field)) return;
    const buttons = [...explorer.querySelectorAll('[data-slab-finish]')];
    if (!buttons.length) return;
    root.dataset.cwReady = 'true';
    const records = buttons.map(button => ({
      button, code: button.dataset.slabFinish, door: button.dataset.door,
      style: button.dataset.styleLabel, finish: button.dataset.finish,
      finishKey: finishKey(button.dataset.finish),
      construction: button.dataset.construction, group: button.dataset.group || '',
      search: normalize([button.dataset.name, button.dataset.styleLabel, button.dataset.finish, button.dataset.slabFinish, button.dataset.categoryLabel].join(' '))
    }));
    const unique = (rows, key) => [...new Map(rows.filter(row => row[key]).map(row => [row[key], row])).values()];
    const styleCards = [...root.querySelectorAll('[data-cw-style-card]')];
    styleCards.forEach(card => {
      const image = card.querySelector('[data-cw-style-preview]');
      const sampleFrame = card.querySelector('[data-cw-style-frame]');
      const fallback = card.querySelector('[data-cw-style-image-error]');
      if (!image || !sampleFrame || !fallback) return;
      image.addEventListener('error', () => {sampleFrame.hidden = true;fallback.hidden = false;});
      image.addEventListener('load', () => {sampleFrame.hidden = false;fallback.hidden = true;});
    });
    const overviewCount = root.querySelector('[data-cw-gallery-count]');
    const overviewEmpty = root.querySelector('[data-cw-gallery-empty]');
    const allStyles = root.querySelector('[data-cw-all-styles]');
    const categories = [...explorer.querySelectorAll('[data-slab-category]')];
    const previewPanel = explorer.querySelector('.cw-slab-preview');
    const preview = explorer.querySelector('[data-slab-preview]');
    const frame = explorer.querySelector('[data-slab-preview-frame]');
    const imageError = explorer.querySelector('[data-slab-image-error]');
    const builder = explorer.querySelector('[data-slab-builder]');
    const count = explorer.querySelector('[data-slab-count]');
    const empty = explorer.querySelector('[data-slab-empty]');
    const categoryGroup = explorer.querySelector('[data-slab-categories]');
    const hiddenMore = form.querySelector('[data-cw-gallery-more]');
    let selected = null;
    let visible = [];
    let observer;

    const setOptions = (field, entries, key = value => value) => {
      const old = field.value;
      const first = field.options[0].cloneNode(true);
      field.replaceChildren(first, ...entries.map(([value, label]) => new Option(label, value)));
      field.value = [...field.options].find(option => key(option.value) === key(old))?.value || '';
    };
    const matchesStructure = (record, door = fields.door.value, construction = fields.construction.value) =>
      (!door || record.door === door) && (!construction || record.construction === construction);
    const updateOptions = changed => {
      if (changed === 'construction' && !records.some(record => matchesStructure(record))) fields.door.value = '';
      if (changed === 'door' && !records.some(record => matchesStructure(record))) fields.construction.value = '';
      if (fields.door.value !== 'slab') fields.group.value = '';
      const doorRows = records.filter(record => !fields.construction.value || record.construction === fields.construction.value);
      setOptions(fields.door, unique(doorRows,'door').sort((a,b) => a.style.localeCompare(b.style)).map(record => [record.door, `${record.style} (${doorRows.filter(row => row.door === record.door).length})`]));
      if (!records.some(record => matchesStructure(record) && (!fields.group.value || record.group === fields.group.value))) fields.group.value = '';
      const available = records.filter(record => matchesStructure(record) && (!fields.group.value || record.group === fields.group.value));
      setOptions(fields.finish, unique(available,'finishKey').map(record => [record.finish, record.finish]), finishKey);
      for (const option of fields.construction.options) option.disabled = !!option.value && !records.some(record => record.construction === option.value);
    };
    const syncUrl = mode => {
      const url = new URL(location.href);
      for (const [name, field] of Object.entries(fields)) {
        const value = field.value.trim();
        if (value) url.searchParams.set(name,value); else url.searchParams.delete(name);
      }
      if (selected) url.searchParams.set('cw_finish',selected.code); else url.searchParams.delete('cw_finish');
      if (!fields.door.value && root.dataset.cwStylesUrl) url.pathname = new URL(root.dataset.cwStylesUrl, location.origin).pathname;
      if (url.href !== location.href) history[mode === 'push' ? 'pushState' : 'replaceState'](history.state,'',url);
    };
    const choose = record => {
      selected = record || null;
      buttons.forEach(button => button.setAttribute('aria-pressed', String(button === record?.button)));
      previewPanel.hidden = !record;
      explorer.toggleAttribute('data-empty', !record);
      if (!record) {
        builder.removeAttribute('href');
        explorer.querySelector('[data-slab-selection]').textContent = '';
        return;
      }
      const button = record.button;
      explorer.querySelector('[data-slab-door-title]').textContent = record.style;
      explorer.querySelector('[data-slab-title]').textContent = record.finish;
      explorer.querySelector('[data-slab-construction-label]').textContent = button.dataset.constructionLabel;
      const groupLabel = explorer.querySelector('[data-slab-category-label]');
      groupLabel.textContent = button.dataset.categoryLabel || '';
      groupLabel.hidden = !record.group;
      const separator = explorer.querySelector('[data-slab-category-separator]');
      if (separator) separator.hidden = !record.group;
      if (frame) {
        frame.style.cssText = button.dataset.previewStyle || '';
        frame.classList.toggle('cw-web-sample-frame', !!button.dataset.previewStyle);
      }
      preview.alt = record.style + ' · ' + record.finish + ' · ' + explorer.dataset.sampleAlt;
      if (!button.dataset.preview) {
        preview.removeAttribute('src');
        preview.hidden = true;
        imageError.hidden = false;
      } else if (preview.getAttribute('src') !== button.dataset.preview) {
        imageError.hidden = true;
        preview.hidden = false;
        preview.src = button.dataset.preview;
      }
      preview.loading = 'eager';
      builder.href = button.href;
      explorer.querySelector('[data-slab-selection]').textContent = record.style + ' · ' + record.finish;
    };
    const filter = (changed, writeUrl = true, mode = 'replace', requestedCode = null) => {
      updateOptions(changed);
      const detail = !!fields.door.value;
      root.toggleAttribute('data-style-selected', detail);
      root.toggleAttribute('data-slab-active', detail);
      explorer.hidden = !detail;
      const overview = root.querySelector('[data-cw-style-overview]');
      if (overview) overview.hidden = detail;
      if (allStyles) allStyles.hidden = !detail;
      const terms = normalize(fields.q.value.trim()).split(/\s+/).filter(Boolean);
      const base = records.filter(record => matchesStructure(record) && (!fields.finish.value || record.finishKey === finishKey(fields.finish.value)) && terms.every(term => record.search.includes(term)));
      visible = base.filter(record => !fields.group.value || record.group === fields.group.value);
      let styleCount = 0;
      for (const card of styleCards) {
        const available = visible.filter(record => record.door === card.dataset.door);
        card.hidden = !available.length;
        if (available.length) styleCount++;
        const sample = available[0];
        if (sample && card.dataset.cwFinish !== sample.code) {
          const image = card.querySelector('[data-cw-style-preview]');
          const sampleFrame = card.querySelector('[data-cw-style-frame]');
          const fallback = card.querySelector('[data-cw-style-image-error]');
          const finishLabel = card.querySelector('[data-cw-style-finish]');
          if (image && sampleFrame && fallback) {
            const source = sample.button.dataset.preview;
            sampleFrame.style.cssText = sample.button.dataset.previewStyle || '';
            sampleFrame.classList.toggle('cw-web-sample-frame', !!sample.button.dataset.previewStyle);
            sampleFrame.hidden = !source;
            fallback.hidden = !!source;
            image.hidden = !source;
            image.alt = sample.style + ' · ' + sample.finish + ' · ' + explorer.dataset.sampleAlt;
            image.removeAttribute('srcset');
            image.removeAttribute('sizes');
            if (source) image.src = source; else image.removeAttribute('src');
          }
          if (finishLabel) finishLabel.textContent = sample.finish;
          card.dataset.cwFinish = sample.code;
        }
        const label = card.querySelector('[data-cw-style-count]');
        if (label) label.textContent = (available.length === 1 ? label.dataset.templateOne : label.dataset.template).replace('{count}', String(available.length));
        const href = new URL(card.href);
        for (const name of ['finish','construction','q']) {
          if (fields[name].value.trim()) href.searchParams.set(name, fields[name].value.trim()); else href.searchParams.delete(name);
        }
        if (sample) href.searchParams.set('cw_finish', sample.code); else href.searchParams.delete('cw_finish');
        card.href = href.href;
      }
      if (overviewCount) overviewCount.textContent = (styleCount === 1 ? overviewCount.dataset.templateOne : overviewCount.dataset.template).replace('{count}', String(styleCount));
      if (overviewEmpty) overviewEmpty.hidden = detail || styleCount > 0;
      const matches = new Set(visible);
      records.forEach(record => { record.button.hidden = !detail || !matches.has(record); });
      count.textContent = count.dataset.template.replace('{count}', String(visible.length));
      empty.hidden = visible.length > 0;
      if (categoryGroup) categoryGroup.hidden = fields.door.value !== 'slab';
      categories.forEach(button => {
        const group = button.dataset.slabCategory;
        const total = base.filter(record => !group || record.group === group).length;
        button.setAttribute('aria-pressed',String(group === fields.group.value));
        button.disabled = !!group && !total;
        const number = button.querySelector('span');
        if (number) number.textContent = '(' + total + ')';
      });
      const requested = requestedCode && visible.find(record => record.code === requestedCode);
      choose(detail ? (requested || (matches.has(selected) ? selected : visible[0])) : null);
      if (writeUrl) syncUrl(mode);
    };
    const fromUrl = () => {
      const params = new URL(location.href).searchParams;
      selected = null;
      for (const field of Object.values(fields)) field.value = '';
      setOptions(fields.door, unique(records,'door').sort((a,b) => a.style.localeCompare(b.style)).map(record => [record.door,record.style]));
      const requested = records.find(record => record.code === params.get('cw_finish'));
      const door = params.get('door');
      if (door && records.some(record => record.door === door)) fields.door.value = door;
      else if (requested) fields.door.value = requested.door;
      else if (!params.has('door') && /\/slab-cabinets\/?$/.test(location.pathname)) fields.door.value = 'slab';
      const construction = params.get('construction');
      if ([...fields.construction.options].some(option => option.value === construction)) fields.construction.value = construction;
      if (fields.door.value && !records.some(record => matchesStructure(record))) fields.construction.value = '';
      if (fields.door.value === 'slab' && [...fields.group.options].some(option => option.value === params.get('group'))) fields.group.value = params.get('group');
      updateOptions('door');
      fields.finish.value = [...fields.finish.options].find(option => finishKey(option.value) === finishKey(params.get('finish')))?.value || '';
      fields.q.value = (params.get('q') || '').slice(0,100);
      filter(null,false,'replace',requested?.code);
    };
    const clear = () => {
      for (const field of Object.values(fields)) field.value = '';
      selected = null;
      filter(null,true,'push');
    };
    root.addEventListener('click', event => {
      const card = event.target.closest('[data-cw-style-card]');
      const back = event.target.closest('[data-cw-all-styles]');
      if ((!card && !back) || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      event.preventDefault();
      fields.door.value = card ? card.dataset.door : '';
      selected = null;
      filter('door', true, 'push');
      const target = card ? explorer.querySelector('.cw-slab-options h3') : root.querySelector('.cw-web-style-overview>h2');
      if (target) {
        target.tabIndex = -1;
        target.focus({preventScroll:true});
        target.scrollIntoView({block:'start'});
      }
    });
    buttons.forEach(button => {
      button.setAttribute('role','button');
      button.addEventListener('keydown',event => {if (event.key === ' ') {event.preventDefault();button.click();}});
    });
    preview.addEventListener('error',() => { preview.hidden = true; imageError.hidden = false; });
    preview.addEventListener('load',() => { preview.hidden = false; imageError.hidden = true; });
    explorer.addEventListener('click',event => {
      const button = event.target.closest('[data-slab-finish]');
      if (button && !event.ctrlKey && !event.metaKey && !event.shiftKey && !event.altKey) {
        event.preventDefault();
        const record = visible.find(row => row.button === button);
        if (record) {choose(record);syncUrl('push');}
        return;
      }
      const category = event.target.closest('[data-slab-category]');
      if (category && !category.disabled) {fields.group.value = category.dataset.slabCategory;filter('group',true,'push');}
      if (event.target.closest('[data-slab-clear]')) {clear();fields.door.focus();}
    });
    form.addEventListener('submit',event => {event.preventDefault();filter(null,true,'push');});
    form.addEventListener('input',event => {if (event.target.name === 'q') filter('q');});
    form.addEventListener('change',event => {if (event.target.name !== 'q') filter(event.target.name,true,'push');});
    form.addEventListener('reset',event => {event.preventDefault();clear();});
    root.querySelector('[data-cw-reset-gallery]')?.addEventListener('click',() => {clear();fields.door.focus();});
    const onPopState = () => {
      if (root.isConnected) fromUrl();
      else {window.removeEventListener('popstate',onPopState);observer?.disconnect();}
    };
    window.addEventListener('popstate',onPopState);
    const header = document.querySelector('header.cw-header');
    if (header && typeof ResizeObserver !== 'undefined') {
      const setOffset = () => explorer.style.setProperty('--cw-slab-top',(header.getBoundingClientRect().height + 16) + 'px');
      setOffset();observer = new ResizeObserver(setOffset);observer.observe(header);
    }
    if (hiddenMore) hiddenMore.hidden = true;
    root.querySelector('[data-slab-entry]')?.setAttribute('hidden','');
    fromUrl();
  };
  const init = (scope = document) => {
    if (scope.matches?.('[data-cw-gallery]')) initGallery(scope);
    scope.querySelectorAll('[data-cw-gallery]').forEach(initGallery);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded',() => init(),{once:true});else init();
  document.addEventListener('shopify:section:load',event => init(event.target));
  document.addEventListener('castlewood:content-ready',event => init(event.target));
})();

